'use client'

import Avatar from '@precis-digital/kurama/Avatar'
import { type ComponentProps, type ReactElement } from 'react'
import { type ThemeAssetSrc } from 'shared/assets/resolveThemeAsset'
import { useResolvedThemeAsset } from 'shared/assets/useResolvedThemeAsset'

type AvatarImageProps = Extract<ComponentProps<typeof Avatar>, { imageUrl: string }>

type ThemedAvatarProps = Omit<AvatarImageProps, 'kind' | 'imageUrl'> & {
  /**
   * Same as Kurama `Avatar` `imageUrl`, or a static import under
   * `public/assets/images`. Prefer `importedAsset.src` for string-only call
   * sites. Dark mode selects the `.dark` sibling unless exempt. Remote /
   * user / GCS URLs pass through unchanged.
   */
  imageUrl: ThemeAssetSrc
  kind?: 'image'
}

/**
 * Drop-in theme-aware replacement for Kurama `Avatar` with `kind="image"`.
 * Keep `kind="image"` and `imageUrl={img.src}` (or `imageUrl={img}`).
 */
const ThemedAvatar = ({ imageUrl, kind: _kind = 'image', ...avatarProps }: ThemedAvatarProps): ReactElement => {
  const resolvedImageUrl = useResolvedThemeAsset(imageUrl)

  return <Avatar {...avatarProps} kind="image" imageUrl={resolvedImageUrl} />
}

export default ThemedAvatar
export type { ThemedAvatarProps }
